import React from 'react';
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { toNumber } from '../../../../../services/CreditsEngineService';
import TablePaginationBar from '../../../../../components/common/TablePaginationBar';
import { useTablePagination } from '../../../../../hooks';

const cellSx = { color: 'var(--wb-text)', borderColor: 'var(--wb-border)' };
const headSx = { ...cellSx, color: 'var(--wb-muted-85)', fontWeight: 600 };

const formatPeriodLabel = (periodKey) => {
  if (!periodKey) return '—';
  const [kind, value] = periodKey.split('#');
  const kindLabel = { HOUR: 'Hora', DAY: 'Día', MONTH: 'Mes' }[kind] || kind;
  return `${kindLabel} · ${value}`;
};

const ConsumptionBucketsTable = ({ buckets, isLoading, showScope = false }) => {
  // Antes de los early returns de abajo: los hooks tienen que ejecutarse
  // en el mismo orden en cada render, y `isLoading` cambia entre renders.
  const { pageRows, paginationProps } = useTablePagination(buckets, {
    storageKey: showScope ? 'consumptionBucketsGlobal' : 'consumptionBucketsTenant',
    resetDeps: [buckets]
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} sx={{ color: 'var(--wb-primary)' }} />
      </Box>
    );
  }

  if (!buckets || buckets.length === 0) {
    return (
      <Typography sx={{ color: 'var(--wb-muted-70)', py: 3, textAlign: 'center' }}>
        No hay consumo registrado para el período seleccionado.
      </Typography>
    );
  }

  return (
    <>
      <TableContainer sx={{ maxHeight: 480 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {showScope && <TableCell sx={headSx}>Alcance</TableCell>}
              <TableCell sx={headSx}>Período</TableCell>
              <TableCell sx={headSx} align="right">Invocaciones</TableCell>
              <TableCell sx={headSx} align="right">Tokens entrada</TableCell>
              <TableCell sx={headSx} align="right">Tokens salida</TableCell>
              <TableCell sx={headSx} align="right">Créditos consumidos</TableCell>
              <TableCell sx={headSx} align="right">Costo real (USD)</TableCell>
              <TableCell sx={headSx}>Actualizado</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pageRows.map((bucket) => (
              <TableRow key={`${bucket.agg_key}#${bucket.period_key}`} hover sx={{ '&:hover': { backgroundColor: 'var(--wb-x-bg-0ea5e90f, rgba(14,165,233,0.06))' } }}>
                {showScope && <TableCell sx={cellSx}>{bucket.agg_key}</TableCell>}
                <TableCell sx={cellSx}>{formatPeriodLabel(bucket.period_key)}</TableCell>
                <TableCell sx={cellSx} align="right">{toNumber(bucket.invocation_count)}</TableCell>
                <TableCell sx={cellSx} align="right">{toNumber(bucket.input_tokens)}</TableCell>
                <TableCell sx={cellSx} align="right">{toNumber(bucket.output_tokens)}</TableCell>
                <TableCell sx={cellSx} align="right">{toNumber(bucket.credits_consumed).toFixed(4)}</TableCell>
                <TableCell sx={cellSx} align="right">${toNumber(bucket.cost_usd).toFixed(4)}</TableCell>
                <TableCell sx={cellSx}>{bucket.updated_at || '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePaginationBar {...paginationProps} />
    </>
  );
};

export default ConsumptionBucketsTable;
